import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { FooterLinks } from "../components/Footer";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-amber-600/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-amber-800/10 blur-3xl" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-2 justify-center mb-8">
          <img src={process.env.PUBLIC_URL + "/cehnura-mark.png"} alt="Cehnura" className="w-11 h-11 object-contain" />
          <span className="font-display font-extrabold text-2xl">Cehn<span className="gold-text">ura</span></span>
        </div>
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-8 relative grain" data-testid="forgot-password-page">
          <div className="relative z-10">
            {sent ? (
              <div className="text-center">
                <MailCheck className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold mb-2">Vérifie ta boîte mail</h1>
                <p className="text-sm text-zinc-400" data-testid="forgot-sent-msg">Si un compte existe avec cet email, tu recevras un lien pour réinitialiser ton mot de passe. Le lien expire dans 1 heure.</p>
                <Link to="/login" className="inline-flex items-center gap-1 text-amber-400 hover:underline mt-6 text-sm" data-testid="back-to-login"><ArrowLeft className="w-4 h-4" />Retour à la connexion</Link>
              </div>
            ) : (
              <>
                <h1 className="font-display text-2xl font-bold mb-1">Mot de passe oublié ?</h1>
                <p className="text-sm text-zinc-500 mb-6">Entre ton email, on t'envoie un lien de réinitialisation.</p>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-zinc-400 mb-1.5">Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" placeholder="ton@email.com" required
                      className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-colors" />
                  </div>
                  {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
                  <button type="submit" disabled={loading} data-testid="forgot-submit"
                    className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer le lien"}
                  </button>
                </form>
                <p className="text-sm text-zinc-500 mt-6 text-center">
                  <Link to="/login" className="text-amber-400 hover:underline" data-testid="back-to-login">Retour à la connexion</Link>
                </p>
              </>
            )}
          </div>
        </div>
        <FooterLinks />
      </motion.div>
    </div>
  );
}
